import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiGet, apiPost, apiUploadImage } from "../../shared/apiClient";
import { getApiBaseUrl } from "../../config/appSettings";
import { useToast } from "../../shared/ToastContext";
import { createBlogQuillModules } from "./blogQuillConfig";
import { BlogEditorFormFields, defaultBlogForm, toLocalDatetime } from "./BlogEditorFormFields";

function RefreshCwIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  );
}

function DocumentCheckIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="m9 15 2 2 4-4" />
    </svg>
  );
}

function EyeIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function NewBlogPage() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    ...defaultBlogForm,
    customerId: isAdmin ? "" : String(user?.customerId ?? ""),
    date: toLocalDatetime(new Date().toISOString())
  });
  const [htmlMode, setHtmlMode] = useState(false);
  const quillRef = useRef(null);

  useEffect(() => {
    void (async () => {
      if (isAdmin) {
        const c = await apiGet("/api/Customers");
        if (c.ok) setCustomers(c.data ?? []);
      }
    })();
  }, [isAdmin]);

  const toastRef = useRef(toast);
  toastRef.current = toast;
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const r = await apiUploadImage("/api/Uploads/image", file);
      if (!r.ok) {
        toastRef.current.push(String(r.error), "error");
        return;
      }
      const quill = quillRef.current?.getEditor();
      if (!quill) return;
      const range = quill.getSelection(true) ?? { index: quill.getLength() };
      const path = r.data?.path ?? "";
      const base = getApiBaseUrl();
      const url = path.startsWith("http") ? path : `${base}${path}`;
      quill.insertEmbed(range.index, "image", url);
    };
  }, []);

  const onEditorImageFiles = useCallback(async (files) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const base = getApiBaseUrl();
    let index = (quill.getSelection(true) ?? { index: Math.max(0, quill.getLength() - 1) }).index;
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      const r = await apiUploadImage("/api/Uploads/image", file);
      if (!r.ok) {
        toastRef.current.push(String(r.error), "error");
        continue;
      }
      const path = r.data?.path ?? "";
      const url = path.startsWith("http") ? path : `${base}${path}`;
      quill.insertEmbed(index, "image", url);
      index += 1;
    }
    quill.setSelection(index, 0);
  }, []);

  const modules = useMemo(() => createBlogQuillModules({ imageHandler }), [imageHandler]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title?.trim()) {
      toast.push("Title is required", "error");
      return;
    }
    const customerId = Number(form.customerId);
    if (!Number.isFinite(customerId)) {
      toast.push("Customer is required", "error");
      return;
    }
    setSaving(true);
    const bodyBase = {
      title: form.title,
      subtitle: form.subtitle || null,
      shortDescription: form.shortDescription || null,
      date: form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
      authorName: form.authorName || null,
      image: form.image || null,
      content: form.content || null
    };
    const res = await apiPost("/api/Blogs", { customerId, ...bodyBase });
    if (!res.ok) toast.push(String(res.error), "error");
    else {
      toast.push("Blog created", "success");
      navigate("/blogs");
    }
    setSaving(false);
  };

  return (
    <div className="w-full space-y-4">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Add blog</h1>
            <p className="mt-1 text-sm text-slate-500">Title and customer (if applicable) are required.</p>
          </div>
          <div className="flex shrink-0 items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => navigate("/blogs")}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-indigo-600 shadow-sm transition hover:bg-indigo-50 hover:text-indigo-700"
              title="Blogs"
              aria-label="Go to blogs list"
            >
              <EyeIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-sky-600 shadow-sm transition hover:bg-sky-50 hover:text-sky-700"
              title="Refresh"
              aria-label="Refresh page"
            >
              <RefreshCwIcon className="h-5 w-5" />
            </button>
            <button
              type="submit"
              form="new-blog-form"
              disabled={saving}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-800 text-white shadow-md transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-600/40 disabled:opacity-50"
              title="Create blog"
              aria-label={saving ? "Creating blog" : "Create blog"}
            >
              {saving ? (
                <span
                  className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white/30 border-t-white"
                  aria-hidden
                />
              ) : (
                <DocumentCheckIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <form id="new-blog-form" onSubmit={onSubmit} className="space-y-0">
          <BlogEditorFormFields
            form={form}
            setForm={setForm}
            isAdmin={isAdmin}
            customers={customers}
            htmlMode={htmlMode}
            setHtmlMode={setHtmlMode}
            quillRef={quillRef}
            modules={modules}
            onEditorImageFiles={onEditorImageFiles}
            onContentImportError={(msg) => toast.push(String(msg), "error")}
            onEditorToolbarImage={imageHandler}
          />
        </form>
      </div>
    </div>
  );
}
